import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { deleteFBAAPI, getFBAAPI, loadFBAAPI, saveFBAAPI } from '../api/fbaApi';

export const getFBA = createAsyncThunk(
  'fba/getFBA',
  async (payload, { rejectWithValue }) => {
    try {
      let email = payload;
      const fba = await getFBAAPI(email);
      return fba;
    } catch (error) {
      return rejectWithValue(payload);
    };
  }
);

export const saveFBA = createAsyncThunk(
  'fba/saveFBA',
  async (payload, { rejectWithValue }) => {
    try {
      const fba = payload;
      if (fba) {
        await saveFBAAPI(fba);
        return fba;
      };
      return null;
    } catch (error) {
      return rejectWithValue(payload?.id);
    };
  }
);

export const loadFBA = createAsyncThunk(
  'fba/loadFBA',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const fbaLocation = payload.location;
      const keepId = payload.keepId;
      const state = getState();
      const email = state?.user?.value?.email;

      if (fbaLocation) {
        const result = await loadFBAAPI(fbaLocation, email, keepId);
        return result;
      };
    } catch (error) {
      return rejectWithValue(payload?.id);
    };
  }
);

export const deleteFBA = createAsyncThunk(
  'fba/deleteFBA',
  async (payload, { rejectWithValue }) => {
    try {
      const fbaId = payload;
      if (fbaId) {
        await deleteFBAAPI(fbaId);
        return fbaId;
      };
    } catch (error) {
      return rejectWithValue(payload?.id);
    };
  }
);

const initialState = {
  byId: {
  },
  allIds: [],
  currentFBAId: '',
};

export const fbaSlice = createSlice({
  name: 'fba',
  initialState,
  reducers: {
    updateFBA: (state, action) => {
      try {
        const fbaId = action.payload.fbaId;
        state.byId[fbaId] = {...action.payload.fba};
        if (state.allIds.indexOf(fbaId) === -1) {
          state.allIds.push(fbaId);
        };
        state.currentFBAId = fbaId;
      } catch (error) {
        return;
      };
    },
    setFBAData: (state, action) => {
      try {
        const fbaId = action.payload?.fba?.id;
        state.byId[fbaId] = {...action.payload.fba};
        if (!state.byId[fbaId]?.settings?.analysisType) {
          state.byId[fbaId].settings.analysisType = {
            name: 'Analysis Type',
            value: 1,
            unit: null
          };
        };
        if (state.allIds.indexOf(fbaId) === -1) {
          state.allIds.push(fbaId);
        };
        state.currentFBAId = fbaId;
      } catch (error) {
        return;
      };
    },
    setCurrentFBAId: (state, action) => {
      try {
        const fbaId = action.payload;
        state.currentFBAId = fbaId;
      } catch (error) {
        return;
      };
    },
    setFBADiagnostics: (state, action) => {
      try {
        const fbaId = action.payload.fbaId;
        state.byId[fbaId].diagnostics = action.payload.diagnostics;
        state.byId[fbaId].breakthroughTmb = action.payload.breakthroughTmb;
      } catch (error) {
        return;
      };
    },
    setFBASettings: (state, action) => {
      try {
        const fbaId = action.payload.fbaId;
        // state.byId[fbaId].lastSavedSettings = state.byId[fbaId].settings;
        state.byId[fbaId].settings = action.payload.settings;
      } catch (error) {
        return;
      };
    },
    // deleteFBA: (state, action) => {
    //   try {
    //     const fbaId = action.payload;
    //     delete state.byId[fbaId];
    //     state.allIds = state.allIds.filter(id => id !== fbaId);
    //     state.currentFBAId = state.allIds.length > 0 ? state.allIds[0] : '';
    //   } catch (error) {
    //     return;
    //   };
    // },
  },
  extraReducers: (builder) => {
    builder.addCase(getFBA.fulfilled, (state, action) => {
      if (action.payload?.length > 0) {
        const fbas = action.payload;
        fbas.forEach(fba => {
          let newSettings = {...fba?.settings};
          
          // fix for older FBAs
          let newLoadFluidPumped = {...newSettings.loadFluidPumped};
          newLoadFluidPumped.unit = 'stb';
          newSettings.loadFluidPumped = newLoadFluidPumped;
          
          if (!newSettings?.fracsAttempted) newSettings = {...newSettings, fracsAttempted: {
            name: 'Fractures Attempted',
            value: 150,
            unit: null  
          }};
          if (!newSettings?.midPointPerfs) newSettings = {...newSettings, midPointPerfs: {
            name: 'Mid Point of Perforations MD',
            value: 0,
            unit: 'ft'  
          }};
          if (!newSettings?.analysisType) newSettings = {...newSettings, analysisType: {
            name: 'Analysis Type',
            value: 1,
            unit: null
          }};
          if (!newSettings?.linearFlowFitInterceptI) newSettings = {...newSettings, linearFlowFitInterceptI: {
            name: 'Linear Flow Intercept Integral',
            value: 202.6,
            unit: null
          }};
          if (!newSettings?.fractureDepletionFitInterceptI) newSettings = {...newSettings, fractureDepletionFitInterceptI: {
            name: 'Fracture Depletion Intercept Integral',
            value: 341.3,
            unit: null
          }};
          if (!newSettings?.derivativeWindowRNP) newSettings = {...newSettings, derivativeWindowRNP: {
            name: 'Derivative Window RNP',
            value: 1,
            unit: null
          }};
          if (!newSettings?.derivativeWindowIRNP) newSettings = {...newSettings, derivativeWindowIRNP: {
            name: 'Derivative Window IRNP',
            value: 0.5,
            unit: null
          }};
          if (!newSettings?.fracType) newSettings = {...newSettings, fracType: {
            name: 'Frac Type',
            value: 1,
            unit: null
          }};

          state.byId[fba.id] = {...fba, settings: newSettings, lastSavedSettings: newSettings};
          if (state.allIds.indexOf(fba.id) === -1) {
            state.allIds.push(fba.id);
          };
        });
        state.currentFBAId = fbas[fbas?.length-1].id;
      };
    })
    builder.addCase(loadFBA.fulfilled, (state, action) => {
      if (action.payload) {
        let newFba = {...action.payload};
        let newSettings = JSON.parse(JSON.stringify(newFba?.settings));
        let newLoadFluidPumped = {...newSettings.loadFluidPumped};
        newLoadFluidPumped.unit = 'stb';
        newSettings.loadFluidPumped = newLoadFluidPumped;

        // fix for older FBAs
        if (!newSettings?.fracsAttempted) newSettings = {...newSettings, fracsAttempted: {
          name: 'Fractures Attempted',
          value: 150,
          unit: null  
        }};
        if (!newSettings?.midPointPerfs) newSettings = {...newSettings, midPointPerfs: {
          name: 'Mid Point of Perforations MD',
          value: 0,
          unit: 'ft'  
        }};
        if (!newSettings?.analysisType) newSettings = {...newSettings, analysisType: {
          name: 'Analysis Type',
          value: 1,
          unit: null
        }};
        if (!newSettings?.linearFlowFitInterceptI) newSettings = {...newSettings, linearFlowFitInterceptI: {
          name: 'Linear Flow Intercept Integral',
          value: 202.6,
          unit: null
        }};
        if (!newSettings?.fractureDepletionFitInterceptI) newSettings = {...newSettings, fractureDepletionFitInterceptI: {
          name: 'Fracture Depletion Intercept Integral',
          value: 341.3,
          unit: null
        }};
        if (!newSettings?.derivativeWindowRNP) newSettings = {...newSettings, derivativeWindowRNP: {
          name: 'Derivative Window RNP',
          value: 1,
          unit: null
        }};
        if (!newSettings?.derivativeWindowIRNP) newSettings = {...newSettings, derivativeWindowIRNP: {
          name: 'Derivative Window IRNP',
          value: 0.5,
          unit: null
        }};
        if (!newSettings?.fracType) newSettings = {...newSettings, fracType: {
          name: 'Frac Type',
          value: 1,
          unit: null
        }};

        for (let key of Object.keys(newFba)) {
          if (key === 'name' || key === 'color' || key === 'description' || key === 'settings') {
            if (state?.byId?.[newFba?.id]?.[key]) {
              newFba[key] = state.byId[newFba.id][key];
            };
          };
        };

        // use the solids and sg water value from the database
        try {
          newSettings.solids_bbt.value = state.byId[newFba.id]?.settings?.solids_bbt?.value;
          newSettings.solids_abt.value = state.byId[newFba.id]?.settings?.solids_abt?.value;
          newSettings.wtPercentSolidsMatrixInflow.value = state.byId[newFba.id]?.settings?.wtPercentSolidsMatrixInflow?.value;
          newSettings.sgWater.value = state.byId[newFba.id]?.settings?.sgWater?.value;
        } catch (error) {
          //
        };

        newFba.lastModified = state.byId[newFba.id]?.lastModified || new Date().toDateString();

        // FORCE FIELD UNITS FOR OLDER FBAS
        if (new Date(newFba.lastModified)?.getTime() < new Date('2024-04-23T20:00:00.000Z').getTime() && newFba.createdBy?.includes('@ssdynamics.com')) {
          newSettings.units.value = 'field';
        };
        
        // // CONVERT FIELD INPUTS TO METRIC
        // if (new Date(newFba.lastModified)?.getTime() < new Date('2024-04-11 00:00:00').getTime() && newFba.createdBy === 'dave.castelino@aspenleafenergy.com') {
        //   newSettings.units.value = 'metric';
        //   for (const param of Object.keys(newSettings)) {
        //     let value = newSettings?.[param]?.value;

        //     if (!value) continue;

        //     if (param === 't_r' || param === 'wht' || param === 'tSep') {
        //       value = parseInt(f_TO_C(value));
        //     } else if (param === 'geothermalGradient') {
        //       value = +(((value) * (5 / 9) * 3.28)?.toFixed(3));
        //     }  else {
        //       if (!fbaConversionFactors?.[param]) continue;
        //       value = newSettings?.[param]?.value * fbaConversionFactors?.[param];
        //     };

        //     newSettings[param] = {...newSettings?.[param], value};
        //   };

        // };
        
        delete newFba.lastUpdated;
        
        newFba.lastSavedSettings = newSettings;
        newFba.settings = newSettings;

        state.byId[newFba.id] = newFba;

        if (state.allIds.indexOf(newFba.id) === -1) {
          state.allIds.push(newFba.id);
        };
        state.currentFBAId = newFba.id;
      };
    })
    builder.addCase(deleteFBA.fulfilled, (state, action) => {
      if (action.payload) {
        const fbaId = action.payload;
        delete state.byId[fbaId];
        state.allIds = state.allIds.filter(id => id !== fbaId);
        state.currentFBAId = state.allIds[0] || "";
      };
    })
    builder.addCase(saveFBA.fulfilled, (state, action) => {
      if (action.payload) {
        const fba = action.payload;
        state.byId[fba.id] = {...fba, lastSavedSettings: fba.settings};
      };
    })
  }
});

export const {
  updateFBA,
  setFBAData,
  setCurrentFBAId,
  setFBADiagnostics,
  setFBASettings
} = fbaSlice.actions;

export const selectFBA = (state) => state.fba;

export default fbaSlice.reducer;